import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, TextField, Typography,
} from '@mui/material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, num, stripsFor } from './common';

interface Props {
  line: any | null;             // a PO line from PoLinesView (id = line id, po_id, po_number, po_status, distributor_name …)
  onClose: () => void;
  onDone: (message: string) => void;
}

/**
 * "Not available at this distributor" for ONE PO line: pick the next distributor (ranked candidates,
 * already-tried ones marked) and move / re-order the quantity there.
 */
const NextDistributorDialog: React.FC<Props> = ({ line, onClose, onDone }) => {
  const [opt, setOpt] = useState<any | null>(null);
  const [distributors, setDistributors] = useState<Array<{ id: number; distributor_name: string }>>([]);
  const [choice, setChoice] = useState<string>('');
  const [other, setOther] = useState<{ id: number; distributor_name: string } | null>(null);
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!line) return;
    setOpt(null); setError(null); setChoice(''); setOther(null);
    Promise.all([orderProcessorAPI.reorderOptions(line.po_id), orderProcessorAPI.distributors()])
      .then(([o, d]) => {
        const l = o.lines.find((x: any) => x.item_id === line.id);
        if (!l) throw new Error('Line not found on the PO');
        setOpt({ ...l, po: o.po }); setDistributors(d);
        setQty(String(l.remaining_qty));
        if (l.suggested_distributor_id) setChoice(String(l.suggested_distributor_id));
      })
      .catch((e) => setError(errMsg(e)));
  }, [line]);

  const ranked = useMemo(() => (opt?.ranked || []).filter((d: any) => d.id !== opt?.po?.distributor_id), [opt]);
  const targetId = choice === 'other' ? other?.id : Number(choice) || null;
  const targetName = choice === 'other' ? other?.distributor_name : ranked.find((d: any) => d.id === Number(choice))?.name;
  const strips = stripsFor(qty, opt?.pack_size);
  const isDraft = opt?.po?.status === 'draft';

  const submit = async () => {
    if (!line || !targetId) return;
    setBusy(true); setError(null);
    try {
      const res = await orderProcessorAPI.reorderLines(line.po_id, [{ item_id: line.id, distributor_id: targetId, qty: Number(qty) }]);
      const r = res[0];
      onDone(`${line.medicine_name}: ${isDraft ? 'moved' : 're-ordered'} to ${targetName} on ${r.po_number}${r.appended ? ' (added to its draft PO)' : ' (new draft PO)'} — marked not available at ${line.distributor_name}.`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!line} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Not available at {line?.distributor_name}?
        <Typography variant="body2" color="text.secondary">
          {line?.medicine_name} · {line?.pack_size || '—'} · on {line?.po_number} ({String(line?.po_status || '').replace(/_/g, ' ')})
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!opt && !error && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>}
        {opt && !opt.eligible && (
          <Alert severity="info">This line can’t be moved — it was already moved/re-ordered or has been received.</Alert>
        )}
        {opt && opt.eligible && (
          <>
            <Alert severity={isDraft ? 'info' : 'warning'} sx={{ mb: 2 }}>
              {isDraft
                ? 'This PO is still a draft: the item is removed from it and added to the chosen distributor’s draft PO (same run).'
                : 'This PO was already sent: the missing quantity is re-ordered from the chosen distributor; this line becomes “not supplied”.'}
            </Alert>
            <Typography variant="subtitle2" gutterBottom>Order from</Typography>
            <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
              {ranked.map((d: any) => (
                <FormControlLabel key={d.id} value={String(d.id)} control={<Radio size="small" />} disabled={false}
                  label={
                    <Box>
                      <Typography variant="body2">
                        {d.rank}. <b>{d.name}</b>{d.tried ? ' — already tried' : ''}{String(d.id) === String(opt.suggested_distributor_id) ? ' (suggested next)' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {d.tier_name || (d.tier ? `tier ${d.tier}` : '')}
                        {d.avg_discount_pct !== null && d.avg_discount_pct !== undefined ? ` · avg ${num(d.avg_discount_pct, 1)}%` : ''}
                        {d.last_date ? ` · last bought ${d.last_date} · PTR ${num(d.last_ptr)} · ${num(d.last_discount_pct, 1)}%` : ''}
                      </Typography>
                    </Box>
                  } />
              ))}
              <FormControlLabel value="other" control={<Radio size="small" />} label={<Typography variant="body2">Another distributor…</Typography>} />
            </RadioGroup>
            {choice === 'other' && (
              <Autocomplete size="small" sx={{ mt: 1 }} options={distributors.filter((d) => d.id !== opt.po.distributor_id)}
                getOptionLabel={(o) => o.distributor_name} value={other} onChange={(_, v) => setOther(v)}
                renderInput={(p) => <TextField {...p} autoFocus placeholder="Search distributor" />} />
            )}
            {!ranked.length && <Typography variant="caption" color="text.secondary">No other ranked distributor has supplied this item — pick one with “Another distributor…”.</Typography>}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
              <TextField size="small" type="number" label="Quantity (units)" value={qty} onChange={(e) => setQty(e.target.value)}
                inputProps={{ min: 1 }} sx={{ width: 160 }} helperText={`ordered ${num(opt.loose_qty, 0)} · received ${num(opt.received_qty, 0)}`} />
              {strips !== null && Number(qty) > 0 && <Typography variant="body2" color="text.secondary">= {strips} strip{strips === 1 ? '' : 's'}</Typography>}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disabled={busy || !opt?.eligible || !targetId || !(Number(qty) > 0)} onClick={submit}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}>
          {targetName ? `${isDraft ? 'Move' : 'Re-order'} to ${targetName}` : 'Choose a distributor'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NextDistributorDialog;
